// @ts-strict-ignore
import {TableRow} from 'common/components/table/types';
import {experimentsClient} from 'common/report/queries/query-api/experiments-client';
import {
  ExperimentQueryMetricParams,
  ExperimentQueryMetricResult,
  ExperimentQueryMetricSegment,
  ExperimentQueryMetricSegmentsParams,
  ExperimentQueryMetricSegmentsResult,
} from 'common/report/queries/query-api/types';
import {debug} from 'common/report/util';
import {Segment} from 'common/query-builder/query-block/segment-menu/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {BookmarkDateRange, DateRangeAttributes, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {getSegmentSectionsOverride} from 'common/report/insights/util/segments';
import {SparkLineData} from 'apps/experiment-reporting/src/components/spark-line-chart/types';
import {SegmentedOverride} from 'common/report/queries/query-api/types';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {diffFromNow, getNow} from 'common/util/timing';
import {ReportName} from 'common/report/constants';
import {Translatable} from 'common/i18n';

export interface TimeFilter {
  displayText: Translatable;
  dateRange: DateRange;
}

export interface MetricResult {
  error: null | string;
  isConversion: boolean;
  loaded: boolean;
  segmented: false;
  treatmentResults: ExperimentTreatmentResult[];
  winningTreatmentIdx: null | number;
}

export interface SegmentedMetricResult {
  error: null | string;
  isConversion: false;
  segmented: true;
  loaded: boolean;
  segments: ExperimentQueryMetricSegment[];
  segmentedOverride: SegmentedOverride;
  segmentCacheIDs: symbol[];
}

export type RowResult = MetricResult | SegmentedMetricResult;

export interface ExperimentTableRow extends TableRow {
  results: RowResult;
  metricIdx: number;
}

export interface Dashboard {
  title: string;
  dashboardID: number;
}

export interface CustomExperiment {
  type: `custom`;
}
export interface ERFExperiment {
  type: `erf_experiment`;
  id: number;
  name: string;
  treatments: Obj[];
}
export interface ABTestExperiment {
  type: `ab_test`;
  id: number;
  name: string;
  variants: number[];
}
export enum AutoDetectedExperimentSource {
  Chameleon = `Chameleon`,
}
export type AutoDetectedExperimentSourceType = AutoDetectedExperimentSource | string;
export interface AutoDetectedExperiment {
  type: `auto_detected`;
  name: string;
  source: null | AutoDetectedExperimentSourceType;
}
export interface AutoDetectedExperimentVariant {
  name: string;
}
export type Experiment = CustomExperiment | ERFExperiment | ABTestExperiment | AutoDetectedExperiment;

export enum SortOption {
  DashboardOrder = `dashboard_order`,
  DescendingWeightedDelta = `desc_weighted_delta`,
}

export enum DeltaDisplayOption {
  Absolute = `Delta`,
  Relative = `Lift`,
}

export interface ExperimentMetric {
  cacheID: symbol;
  bookmarkID: null | number;
  funnelID: null | number;
  reportType: BookmarkType;
  reportSubtype: string;
  segmented: boolean;
  bookmarkSegmented: boolean;
  title: string;
  validationError: null | string;
}

export enum TreatmentFilterType {
  Cohort = `cohort`,
  Property = `property`,
  ABTest = `started_ab_test`,
  ERFExperiment = `erf_experiment`,
}

export interface ExperimentTreatment {
  label: string;
  // Todo Jordan: consolidate filter with /iron/common/widgets/property-filter-menu/models/property-filter.ts
  // instead of (PropertyFilter from 'common/widgets/property-filter-menu/models/property-filter')
  filterLabel: string; // generating the label can be expensive, so the label is cached when "filter" is set
  filters: any[];
}

export interface ExperimentTreatmentResult {
  rate: number;
  rateUnit: string;
  delta?: number;
  deltaChartData?: SparkLineData;
  confident?: boolean;
  confidenceChartData?: SparkLineData;
  firstConfidentDate?: null | string;
  latestConfidence?: number;
  latestConfidenceDate?: string;
}

export interface JsUrlData {
  time?: DateRangeAttributes;
  dashboardId?: number;
  experiment?: Experiment;
  treatments?: ExperimentTreatment[];
  sortOption?: SortOption;
  deltaDisplayOption?: DeltaDisplayOption;
  trendUnit?: TimeUnit;
  groupBy?: Segment[];
}

export function getMetricQueryParams(
  projectId: number,
  metric: ExperimentMetric,
  control: ExperimentTreatment,
  treatments: ExperimentTreatment[],
  dateRangeOverride?: BookmarkDateRange,
  trendUnit?: string,
  segment?: ExperimentQueryMetricSegment,
  segmentedOverride?: SegmentedOverride,
  workspaceId?: number,
  isMainQueryForReport?: boolean,
): ExperimentQueryMetricParams {
  return {
    projectId,
    bookmarkId: metric.bookmarkID,
    control: {
      filters: control.filters,
    },
    treatments: treatments.map((tr) => ({
      filters: tr.filters,
    })),
    dateRangeOverride,
    trendUnit,
    segment,
    segmentedOverride,
    isSegmentsQuery: false,
    workspaceId,
    trackingProps: {
      report_name: ReportName.ExperimentReporting,
      is_main_query_for_report: isMainQueryForReport,
    },
  };
}

export function getMetricSegmentsQueryParams(
  projectId: number,
  metric: ExperimentMetric,
  dateRangeOverride?: BookmarkDateRange,
  trendUnit?: string,
  workspaceId?: number,
  isMainQueryForReport?: boolean,
  groupBy?: Segment[],
): ExperimentQueryMetricSegmentsParams {
  let segmentedOverride: SegmentedOverride;
  if (groupBy?.length > 0) {
    if (metric.reportType === BookmarkType.Insights) {
      segmentedOverride = getSegmentSectionsOverride(groupBy);
    } else {
      segmentedOverride = groupBy;
    }
  }

  return {
    projectId,
    bookmarkId: metric.bookmarkID,
    dateRangeOverride,
    trendUnit,
    isSegmentsQuery: true,
    workspaceId,
    trackingProps: {
      report_name: ReportName.ExperimentReporting,
      is_main_query_for_report: isMainQueryForReport,
    },
    ...(segmentedOverride ? {segmentedOverride} : {}),
  };
}

export async function fetchMetricQuery(
  params: ExperimentQueryMetricParams,
  clearCache = false,
): Promise<ExperimentQueryMetricResult> {
  let latency: number;
  const start = getNow();
  try {
    const results = await experimentsClient.query(params, {clearCache});
    latency = Math.round(diffFromNow(start));
    debug.info(`Metric query completed.`, latency);
    if (results.error) {
      throw new Error(results.error);
    }
    return results;
  } catch (error) {
    latency = Math.round(diffFromNow(start));
    debug.error(`Metric query failed.`, latency, error);
    throw error;
  }
}

export async function fetchMetricSegmentsQuery(
  params: ExperimentQueryMetricSegmentsParams,
  clearCache = false,
): Promise<ExperimentQueryMetricSegmentsResult> {
  let latency: number;
  const start = getNow();
  try {
    const results = await experimentsClient.query(params, {clearCache});
    latency = Math.round(diffFromNow(start));
    debug.info(`Metric segments query completed.`, latency);
    if (results.error) {
      throw new Error(results.error);
    }
    return results;
  } catch (error) {
    latency = Math.round(diffFromNow(start));
    debug.error(`Metric segments query failed.`, latency, error);
    throw error;
  }
}
